import Icon from "@web-ui/components/Icon";
import type { Tone } from "@web-ui/types/tone";

/*
 * Notice: an inline message block (owner 2026-10-13; the apps drew it on the kit's label classes). The
 * tone sits on the hairline, the tint and the leading icon (styles/data-display.css .notice, on the
 * --tone-* tokens); the words stay in the text ink. A critical notice is an alert, the rest are status.
 * It reveals on mount (motion-reveal). The action slot takes a button (Retry, Undo); onDismiss adds the
 * close button at the end.
 */
type NoticeProps = {
    className?: string;
    tone: Tone;
    title?: React.ReactNode;
    /** A button at the end of the line (Retry, Undo, View). */
    action?: React.ReactNode;
    onDismiss?: () => void;
    /** The close button's accessible name, for apps that translate (English by default). */
    dismissLabel?: string;
    children?: React.ReactNode;
};

const ICON: Record<Tone, string> = {
    neutral: "info",
    info: "info",
    positive: "check-circle",
    warning: "warning",
    critical: "close-circle",
};

const Notice = ({ className, tone, title, action, onDismiss, dismissLabel = "Dismiss", children }: NoticeProps) => (
    <div className={`notice motion-reveal ${className || ""}`} data-tone={tone} role={tone === "critical" ? "alert" : "status"}>
        <Icon className="notice__icon" name={ICON[tone]} size={5} />
        <div className="notice__body">
            {title ? <div className="notice__title">{title}</div> : null}
            {children ? <div className="notice__text">{children}</div> : null}
        </div>
        {action ? <div className="notice__action">{action}</div> : null}
        {onDismiss ? (
            <button type="button" className="notice__dismiss" aria-label={dismissLabel} onClick={onDismiss}>
                <Icon name="close" size={4} />
            </button>
        ) : null}
    </div>
);

export default Notice;
